import * as TronWeb from "tronweb";
import { Service, Container } from "typedi";
import { IEvent } from "./TronService";
import { TronManagerService } from "./TronManagerService";

export interface ITransaction {
  date: number;
  status: string;
  from: string;
  to: string;
  amount: number;
  action: string;
  eventIndex: number;
  txid: string;
  token: string;
  block: number;
}

function fromHex(hex: string): string {
  return TronWeb.address.fromHex(hex);
}

class Transaction implements ITransaction {
  date: number;
  status: string;
  from: string;
  to: string;
  amount: number;
  action: string;
  eventIndex: number;
  txid: string;
  token: string;
  block: number;

  constructor(event: IEvent) {
    this.date = Number(event.block_timestamp);
    this.status = "SUCCESS";
    this.from = "";
    this.to = "";
    this.amount = -1;
    this.action = event.event_name;
    this.eventIndex = event.event_index;
    this.txid = event.transaction_id;
    this.block = event.block_number;
  }
}

class Transfer extends Transaction {
  constructor(event: IEvent) {
    super(event);
    this.from = fromHex(event.result.from);
    this.to = fromHex(event.result.to);
    this.amount = event.result.value;
  }
}

export interface IConvert {
  date: number;
  status: string;
  address: string;
  convertAmount: number;
  receiveAmount: number;
  action: string;
  eventIndex: number;
  txid: string;
  token: string;
  block: number;
}

class Convert implements IConvert {
  date: number;
  status: string;
  address: string;
  convertAmount: number;
  receiveAmount: number;
  action: string;
  eventIndex: number;
  txid: string;
  token: string;
  block: number;

  constructor(event: IEvent) {
    this.date = Number(event.block_timestamp);
    this.status = "SUCCESS";
    this.address = "";
    this.token = "";
    this.convertAmount = -1;
    this.receiveAmount = -1;
    this.action = event.event_name;
    this.eventIndex = event.event_index;
    this.txid = event.transaction_id;
    this.block = event.block_number;
  }
}

class ConvertToCash extends Convert {
  constructor(event: IEvent) {
    super(event);
    this.address = fromHex(event.result.converter);
    this.token = "rise";
    this.convertAmount = event.result.riseAmountSent;
    this.receiveAmount = event.result.cashAmountReceived;
  }
}

class ConvertToRise extends Convert {
  constructor(event: IEvent) {
    super(event);
    this.address = fromHex(event.result.converter);
    this.token = "cash";
    this.convertAmount = event.result.cashAmountSent;
    this.receiveAmount = event.result.riseAmountReceived;
  }
}

function eventToTransactionFactory(event: IEvent) {
  switch (event.event_name) {
    case "ConvertToRise":
      return new ConvertToRise(event);
    case "ConvertToCash":
      return new ConvertToCash(event);
    case "Transfer":
      return new Transfer(event);
    default:
      return undefined;
  }
}

function createTransactionService() {
  return new TransactionService(Container.get(TronManagerService));
}

@Service({ factory: createTransactionService })
export class TransactionService {
  constructor(private tronManagerService: TronManagerService) {}

  /**
   * @param minTimestamp
   * @param maxTimestamp
   *
   * @returns [transactionList,lastValidEventTime]
   * consider valid range from minTimestamp to lastValidEventTime
   */
  async getTransactions(
    contractAddress: string,
    minTimestamp: number,
    maxTimestamp?: number,
    limit: number = 200,
    maxResults: number = 1000,
    orderBy?: string,
    eventName?: string
  ): Promise<[ITransaction[], number]> {
    const options = {
      min_timestamp: minTimestamp,
      max_timestamp: maxTimestamp,
      limit: limit,
      order_by: orderBy,
      event_name: eventName,
    };
    const max = maxResults;

    const tronService = this.tronManagerService.getTronService();
    const events = await tronService.getAllEvents(
      contractAddress,
      options,
      max
    );
    events.sort(sortDesc);

    const transactions = events
      .map((e) => eventToTransactionFactory(e))
      .filter((e): e is Transaction => Boolean(e));

    //const maxValidT = events.length >= max ? events[0].block_timestamp : 0;
    const count = events.length;

    return [transactions, count];
  }
}

function sortDesc(a: IEvent, b: IEvent) {
  return b.block_timestamp - a.block_timestamp;
}
